import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';

export default function TenantDialog({ open, tenant, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    abbreviation: '',
    tenant: '',
    mainColor: '#1976d2',
    secondaryColor: '#dc004e',
    logo: '',
    preferredLanguage: 'en',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (tenant) {
      setFormData({
        name: tenant.name || '',
        abbreviation: tenant.abbreviation || '',
        tenant: tenant.tenant || '',
        mainColor: tenant.mainColor || '#1976d2',
        secondaryColor: tenant.secondaryColor || '#dc004e',
        logo: tenant.logo || '',
        preferredLanguage: tenant.preferredLanguage || 'en',
        adminFirstName: '',
        adminLastName: '',
        adminEmail: '',
      });
    } else {
      setFormData({
        name: '',
        abbreviation: '',
        tenant: '',
        mainColor: '#1976d2',
        secondaryColor: '#dc004e',
        logo: '',
        preferredLanguage: 'en',
        adminFirstName: '',
        adminLastName: '',
        adminEmail: '',
      });
    }
    // Reset loading and error states when dialog opens
    setLoading(false);
    setError('');
  }, [tenant, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await onSave(formData);
    } catch (err) {
      // Extract the error message from axios response
      const errorMessage = err.response?.data?.error || err.message || 'Failed to save tenant';
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {tenant ? 'Edit Tenant' : 'Create New Tenant'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Tenant Information */}
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Tenant Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                disabled={!!tenant}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Abbreviation"
                name="abbreviation"
                value={formData.abbreviation}
                onChange={handleChange}
                disabled={!!tenant}
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Tenant ID (slug)"
                name="tenant"
                value={formData.tenant}
                onChange={handleChange}
                disabled={!!tenant}
                helperText="Lowercase, no spaces (e.g., betterfield, catp)"
              />
            </Grid>

            {/* Branding */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="color"
                label="Main Color"
                name="mainColor"
                value={formData.mainColor}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="color"
                label="Secondary Color"
                name="secondaryColor"
                value={formData.secondaryColor}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Logo URL"
                name="logo"
                value={formData.logo}
                onChange={handleChange}
                placeholder="https://example.com/logo.png"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Preferred Language"
                name="preferredLanguage"
                value={formData.preferredLanguage}
                onChange={handleChange}
                SelectProps={{ native: true }}
              >
                <option value="en">English</option>
                <option value="fr">Français</option>
                <option value="pt">Português</option>
              </TextField>
            </Grid>

            {/* Admin User (only for new tenants) */}
            {!tenant && (
              <>
                <Grid item xs={12}>
                  <TextField
                    disabled
                    fullWidth
                    value="Admin User Information"
                    variant="standard"
                    sx={{ fontWeight: 'bold', mt: 2 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Admin First Name"
                    name="adminFirstName"
                    value={formData.adminFirstName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Admin Last Name"
                    name="adminLastName"
                    value={formData.adminLastName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    type="email"
                    label="Admin Email"
                    name="adminEmail"
                    value={formData.adminEmail}
                    onChange={handleChange}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {tenant ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
